import {useEffect} from "react";
import type {Root} from "../../cdx-models/Root";
import {api} from "../api";
import {setTimeFormatPreference} from "../date-format";
import {waitForResult} from "../wait-for-result";

const getDeviceTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
};

/**
 * Writes the device's time zone to the user once per session, and applies the user's 12h/24h
 * preference to the formatters in `date-format.ts`.
 */
const useSyncUserTimeSettings = (root: Root) => {
  const me = root.loggedInUser;
  const meId = me ? me.$meta.get("id", null) : null;
  // `undefined` while loading, so the preference seeded from localStorage stays until then.
  const timeFormat = me ? me.$meta.get("timeFormat", undefined) : null;

  useEffect(() => {
    if (timeFormat !== undefined) setTimeFormatPreference(timeFormat);
  }, [timeFormat]);

  useEffect(() => {
    if (!meId) return;
    return waitForResult(
      () => {
        const user = api.getRoot().loggedInUser;
        return user ? {id: user.id, timezone: user.timezone} : null;
      },
      (user) => {
        if (user?.id !== meId) return;
        const deviceTimeZone = getDeviceTimeZone();
        if (deviceTimeZone && deviceTimeZone !== user.timezone) {
          void api.mutate.users.update({id: meId, timezone: deviceTimeZone});
        }
      }
    );
  }, [meId]);
};

export default useSyncUserTimeSettings;
